import App from 'antd/lib/app';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import Skeleton from 'antd/lib/skeleton';
import Tag from 'antd/lib/tag';
import { SUCCESS_COLOR } from '../../../../theme';
import { Market } from '../../../../domain/market-prices/interface';
import { Card } from 'antd/lib';
import { useCompanies } from '../../../../components/selectors/GlobalCompanySelector/CompanyContext';
import { useMarketsListQuery } from '../../../../domain/filter-options/queries';
import { addDays, format } from 'date-fns';
import { WeatherForecastRow } from './WeatherForecastRow';
import { IUseAuth, useAuth } from '../../../../domain/auth/useAuth';
import './WeatherForecast.css';
import './weatherIcons.css';

export interface ILocation {
    city: string;
    state: string;
    market?: Market;
    lat: number;
    lng: number;
}

export interface IWeatherForecastProps {
    location?: ILocation;
    activeEventMarketsMap: Map<string, string[]>;
}

export function WeatherForecast({ location, activeEventMarketsMap }: IWeatherForecastProps) {
    const { notification } = App.useApp();
    const { companyId } = useCompanies()!;
    const auth = useAuth();

    const { data: marketsData, isLoading: isMarketsLoading, isError: isMarketsError } = useMarketsListQuery({ company_id: companyId });
    const markets = marketsData?.map(item => item.market) || [];

    if (isMarketsError) {
        notification.error({ key: 'site-markets', message: 'Cannot get markets!' });
    }

    const locations: ILocation[] = [...prepareLocationsBasedOnMarkets(markets), location!];

    const days = prepareDays();

    return (
        <Card title={`Energy Program Weekly Forecast`} extra={<WeatherForecastLegend />}>
            {isMarketsLoading ? (
                <Skeleton loading paragraph={{ rows: 10 }} active />
            ) : (
                <>
                    <Row gutter={8}>
                        {days.map(dayName => (
                            <Col
                                style={{
                                    flexBasis: '20%',
                                    width: '20%',
                                }}
                                key={dayName}
                            >
                                <div className="weather-forecast-day-card" style={{ textAlign: 'center', padding: 0 }}>
                                    <h2 style={{ margin: '0' }}>{dayName}</h2>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <div style={{ height: getHeightOfContent(companyId, auth), overflowY: 'auto', overflowX: 'hidden' }}>
                        {locations.map((location, idx) => (
                            <WeatherForecastRow
                                location={location}
                                activeEventMarketsMap={activeEventMarketsMap}
                                key={`${location?.lat};${location?.lng};${idx}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </Card>
    );
}

function WeatherForecastLegend() {
    return (
        <div>
            <Tag color="#888787">None</Tag>
            <Tag color={SUCCESS_COLOR}>Low</Tag>
            <Tag color="#fac42a">Medium</Tag>
            <Tag color="#fa675c">High</Tag>
        </div>
    );
}

function prepareDays(): string[] {
    // return current and 4 next days in short format
    return Array.from({ length: 5 }, (_, i) => format(addDays(new Date(), i), 'EEE'));
}

function prepareLocationsBasedOnMarkets(markets: Market[]): ILocation[] {
    const locations: (ILocation | null)[] = markets.map(market => {
        switch (market) {
            case Market.CAISO:
                // Bakersfield, California
                return {
                    city: 'Bakersfield',
                    state: 'CA',
                    market,
                    lat: 35.373291,
                    lng: -119.018707,
                };
            case Market.ERCOT:
                // Austin, Texas
                return {
                    city: 'Austin',
                    state: 'TX',
                    market,
                    lat: 30.267151,
                    lng: -97.743057,
                };
            case Market.NYISO:
                // New York City, New York
                return {
                    city: 'New York City',
                    state: 'NY',
                    market,
                    lat: 40.714272,
                    lng: -74.005966,
                };
            case Market.ISONE:
                // Worcester, Massachusetts
                return {
                    city: 'Worcester',
                    state: 'MA',
                    market,
                    lat: 42.262589,
                    lng: -71.802292,
                };
            case Market.PJM:
                // Pittsburgh, Pennsylvania
                return {
                    city: 'Pittsburgh',
                    state: 'PA',
                    market,
                    lat: 40.44062,
                    lng: -79.995888,
                };
            default:
                return null;
        }
    });

    return locations.filter(location => location?.market) as ILocation[];
}

function getHeightOfContent(companyId: number | null, auth: IUseAuth | null) {
    if (auth?.user?.isUtilityCustomer()) {
        return companyId ? '422px' : '635px';
    }

    if (auth?.user?.isControlProvider()) {
        return '422px';
    }

    return '635px';
}
